import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { HydraCollectionResponse } from '@vs-shop/schemas/openapi/response';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { matchStatus } from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/taxons-menu';
const HTTP_METHOD = 'get';

export type MakeListCategoryRootMenuDeps<TaxonMenu extends Schema.Top> = {
  readonly taxonMenu: TaxonMenu;
};

export const makeListCategoryRootMenu = <TaxonMenu extends Schema.Top>(
  deps: MakeListCategoryRootMenuDeps<TaxonMenu>
) => {
  const ListCategoryRootMenuError = createBackendApiError({
    domain: 'Category',
    endpoint: 'ListRootMenu',
    path: ENDPOINT,
    identifierField: 'page',
  });

  const listCategoryRootMenuFetch = Effect.fn(`http.${HTTP_METHOD} ${ENDPOINT}`, {
    kind: 'client',
    attributes: {
      'http.request.method': HTTP_METHOD,
    },
  })(function* () {
    const apiClient = yield* BackendApi;
    return yield* apiClient
      .path(ENDPOINT)
      .method(HTTP_METHOD)
      .accept('application/ld+json')
      .fetch({});
  });

  const listCategoryRootMenu = () =>
    listCategoryRootMenuFetch().pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        matchStatus({
          '2xx': (response) =>
            schemaBodyJsonClean(HydraCollectionResponse(deps.taxonMenu))(response),
          404: (response) =>
            Effect.fail(new ListCategoryRootMenuError.NotFoundError({ page: 1, response })),
          orElse: (response) =>
            Effect.fail(new ListCategoryRootMenuError.ApiRequestError({ page: 1, response })),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.CategoryListRootMenu,
          'Debug',
          `Successfully resolved ${String(result['hydra:member'].length)} root menu items`
        )
      ),
      Effect.withSpan('api.category.listRootMenu', {
        kind: 'internal',
      })
    );

  return { listCategoryRootMenu, ListCategoryRootMenuError };
};
